import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { getMySnaps, getUserSnaps, getTimeRemaining, deleteSnap } from '../../api/linguaSnaps';
import LinguaSnapViewer from './LinguaSnapViewer';
import LinguaSnapUpload from './LinguaSnapUpload';
import './LinguaSnaps.css';
import { FaPlus, FaTrash, FaClock, FaEye } from 'react-icons/fa';

const ProfileLinguaSnaps = ({ profileId, isOwnProfile, profileUser }) => {
  const { user } = useAuth();
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCount, setActiveCount] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedSnapIndex, setSelectedSnapIndex] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [openMenuSnapId, setOpenMenuSnapId] = useState(null);

  const loadSnaps = async () => {
    if (!profileId) return;
    try {
      setLoading(true);
      setError(null);

      if (isOwnProfile) {
        const { snaps: data, activeCount: count } = await getMySnaps(false);
        setSnaps(data || []);
        setActiveCount(count || 0);
      } else {
        const { snaps: data } = await getUserSnaps(profileId);
        setSnaps(data || []);
      }
    } catch (err) {
      console.error('[ProfileLinguaSnaps] Error loading snaps:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnaps();
  }, [profileId, isOwnProfile]);

  const handleOpenViewer = (index) => {
    setSelectedSnapIndex(index);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
  };

  const handleUploadComplete = () => {
    setUploadOpen(false);
    loadSnaps();
  };

  const handleDelete = async (e, snapId) => {
    e?.stopPropagation?.();
    if (!window.confirm('Delete this LinguaSnap?')) return;
    try {
      await deleteSnap(snapId);
      setSnaps((prev) => prev.filter((s) => s.id !== snapId));
      setActiveCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('[ProfileLinguaSnaps] Error deleting snap:', err);
    }
  };

  // Filter active snaps for display
  const activeSnaps = snaps.filter((s) => {
    if (s.status !== 'published') return false;
    if (!s.expires_at) return false;
    return new Date(s.expires_at) > new Date();
  });

  // Prepare feed format for viewer - use profileUser prop or fallback to auth user if own profile
  const viewerUser = profileUser || (isOwnProfile ? user : null);
  const feedForViewer = activeSnaps.length > 0 ? [{
    user: {
      id: profileId,
      display_name: viewerUser?.display_name || viewerUser?.name || 'User',
      avatar_url: viewerUser?.avatar_url || null,
    },
    snaps: activeSnaps,
  }] : [];

  if (loading && snaps.length === 0) {
    return (
      <div className="profile-lingua-snaps">
        <div className="profile-lingua-snaps-header">
          <h3>LinguaSnaps</h3>
        </div>
        <div className="lingua-snaps-loading">Loading...</div>
      </div>
    );
  }

  // Don't show section if not own profile and no active snaps
  if (!isOwnProfile && activeSnaps.length === 0) {
    return null;
  }

  return (
    <>
      <div className="profile-lingua-snaps">
        <div className="profile-lingua-snaps-header">
          <h3>
            LinguaSnaps
            {isOwnProfile && (
              <span className="snap-count-badge">{activeCount}/10</span>
            )}
          </h3>
          {isOwnProfile && activeCount < 10 && (
            <button
              className="lingua-snap-add-btn"
              onClick={() => setUploadOpen(true)}
              title="Create LinguaSnap"
            >
              <FaPlus /> New
            </button>
          )}
        </div>

        <div className="profile-lingua-snaps-row" onClick={() => setOpenMenuSnapId(null)}>
          {isOwnProfile && activeCount < 10 && (
            <div
              className="profile-lingua-snap-item snap-create-placeholder"
              onClick={() => setUploadOpen(true)}
              title="Create LinguaSnap"
            >
              <div className="snap-placeholder">
                <FaPlus />
                <span>Create</span>
              </div>
            </div>
          )}

          {activeSnaps.map((snap, index) => {
            const timeLeft = getTimeRemaining(snap.expires_at);
            const menuOpen = openMenuSnapId === snap.id;
            return (
              <div key={snap.id} className="profile-lingua-snap-wrapper">
                <div
                  className="profile-lingua-snap-item"
                  onClick={() => handleOpenViewer(index)}
                >
                  {snap.thumbnailUrl ? (
                    <img src={snap.thumbnailUrl} alt="Snap thumbnail" />
                  ) : (
                    <div className="snap-placeholder">
                      <FaEye />
                    </div>
                  )}

                  {isOwnProfile && (
                    <button
                      className="snap-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuSnapId((prev) => (prev === snap.id ? null : snap.id));
                      }}
                      title="Actions"
                      type="button"
                    >
                      <span className="snap-menu-ellipsis" aria-hidden="true">...</span>
                    </button>
                  )}

                  <div className="snap-overlay">
                    {timeLeft && (
                      <span className="snap-time">
                        <FaClock /> {timeLeft}
                      </span>
                    )}
                  </div>
                </div>

                {isOwnProfile && menuOpen && (
                  <div className="snap-menu" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="snap-menu-item danger"
                      onClick={(e) => {
                        handleDelete(e, snap.id);
                        setOpenMenuSnapId(null);
                      }}
                      type="button"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {activeSnaps.length === 0 && !isOwnProfile && (
            <div className="lingua-snaps-empty">
              No LinguaSnaps yet.
            </div>
          )}
        </div>

        {error && <div className="lingua-snap-error-small">{error}</div>}
      </div>

      {/* Viewer */}
      {viewerOpen && feedForViewer.length > 0 && (
        <LinguaSnapViewer
          feed={feedForViewer}
          initialUserIndex={0}
          onClose={handleCloseViewer}
        />
      )}

      {/* Upload */}
      {uploadOpen && (
        <LinguaSnapUpload
          onClose={() => setUploadOpen(false)}
          onComplete={handleUploadComplete}
        />
      )}
    </>
  );
};

export default ProfileLinguaSnaps;
